if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.onButtonClick = this.onButtonClick.bind(this);
        this.onBuyNowClick = this.onBuyNowClick.bind(this);
        this.onNotifyClick = this.onNotifyClick.bind(this);
        this.onVariantLoading = this.onVariantLoading.bind(this);
        this.onVariantLoaded = this.onVariantLoaded.bind(this);
        this.scheduleVisibilityUpdate = this.scheduleVisibilityUpdate.bind(this);
        this.scheduleSummarySync = this.scheduleSummarySync.bind(this);
        this.syncFromMainButton = this.syncFromMainButton.bind(this);
        this.updateStickyOffset = this.updateStickyOffset.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.isQuickAdd = Boolean(this.closest('quick-add-modal'));
        this.button = this.querySelector('[data-sticky-submit]');
        this.buyNowButton = this.querySelector('[data-sticky-buy-now]');
        this.notifyProxyButton = this.querySelector('[data-sticky-notify-proxy]');
        this.actions = this.querySelector('.product-sticky-atc__actions');
        this.buttonText = this.querySelector('[data-sticky-button-text]');
        this.spinner = this.querySelector('.loading__spinner');
        this.price = this.querySelector('[data-sticky-price]');
        this.image = this.querySelector('[data-sticky-image]');
        this.soldOutBadge = this.querySelector('[data-sticky-sold-out-badge]');
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.mainPrice = document.getElementById(`price-${this.sectionId}`);

        this.button?.addEventListener('click', this.onButtonClick);
        this.buyNowButton?.addEventListener('click', this.onBuyNowClick);
        this.notifyProxyButton?.addEventListener('click', this.onNotifyClick);
        this.productInfo?.addEventListener('change', this.scheduleSummarySync);
        this.productInfo?.addEventListener('product-info:variant-loading', this.onVariantLoading);
        this.productInfo?.addEventListener('product-info:variant-loaded', this.onVariantLoaded);
        window.addEventListener('scroll', this.scheduleVisibilityUpdate, { passive: true });
        window.addEventListener('resize', this.scheduleVisibilityUpdate);

        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(this.updateStickyOffset);
          this.resizeObserver.observe(this);
        }

        if (typeof MutationObserver !== 'undefined' && this.actions) {
          this.actionsObserver = new MutationObserver(() => {
            this.syncNotifyButton();
            this.checkVariantButtonsReady();
          });
          this.actionsObserver.observe(this.actions, {
            attributes: true,
            childList: true,
            subtree: true,
            attributeFilter: ['class', 'hidden', 'style'],
          });
        }

        this.observeMainButton();
        this.observeMainPrice();

        this.variantChangeUnsubscriber =
          typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined'
            ? subscribe(PUB_SUB_EVENTS.variantChange, this.scheduleSummarySync)
            : undefined;

        this.syncFromMainButton();
        this.syncPrice();
        this.syncVariantImage();
        this.initializeVariantState();
        this.updateStickyOffset();
        this.scheduleVisibilityUpdate();
        this.removeAttribute('data-sticky-init-pending');
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.onButtonClick);
        this.buyNowButton?.removeEventListener('click', this.onBuyNowClick);
        this.notifyProxyButton?.removeEventListener('click', this.onNotifyClick);
        this.productInfo?.removeEventListener('change', this.scheduleSummarySync);
        this.productInfo?.removeEventListener('product-info:variant-loading', this.onVariantLoading);
        this.productInfo?.removeEventListener('product-info:variant-loaded', this.onVariantLoaded);
        window.removeEventListener('scroll', this.scheduleVisibilityUpdate);
        window.removeEventListener('resize', this.scheduleVisibilityUpdate);
        this.buttonObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.actionsObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        window.clearTimeout(this.variantUpdateFallbackTimer);
        if (this.visibilityFrame) window.cancelAnimationFrame(this.visibilityFrame);
        if (this.summaryFrame) window.cancelAnimationFrame(this.summaryFrame);
        document.body?.classList.remove('product-sticky-atc-visible');
        document.documentElement.style.removeProperty('--product-sticky-atc-height');
      }

      onButtonClick() {
        this.syncFromMainButton();

        if (!this.mainButton || this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.mainButton.click();
      }

      onBuyNowClick() {
        this.syncFromMainButton();

        if (!this.mainButton || this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const variantId = this.productForm?.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        const quantityInput = this.quantityInput;
        const quantity = Math.max(parseInt(quantityInput?.value || '1', 10) || 1, 1);
        const cartUrl = (window.routes?.cart_url || '/cart').replace(/\/$/, '');

        window.location.href = `${cartUrl}/${variantId}:${quantity}?checkout`;
      }

      onNotifyClick() {
        const notifyButton = this.actions?.querySelector('.restock-alerts-notify-button');
        if (notifyButton) {
          notifyButton.click();
          return;
        }

        this.pendingNotifyClick = true;
        this.notifyProxyButton?.setAttribute('aria-busy', 'true');
      }

      onVariantLoading(event) {
        window.clearTimeout(this.variantUpdateFallbackTimer);
        this.variantResponseReceived = false;

        if (event.detail?.preserveInteractiveState) {
          this.classList.remove('is-updating');
          this.actions?.removeAttribute('aria-busy');
          return;
        }

        this.classList.add('is-updating');
        this.actions?.setAttribute('aria-busy', 'true');

        this.variantUpdateFallbackTimer = window.setTimeout(() => this.finishVariantUpdate(), 5000);
      }

      initializeVariantState() {
        this.variantResponseReceived = true;
        this.classList.add('is-updating');
        this.actions?.setAttribute('aria-busy', 'true');
        this.variantUpdateFallbackTimer = window.setTimeout(() => this.finishVariantUpdate(), 5000);
        this.checkVariantButtonsReady();
      }

      onVariantLoaded() {
        this.variantResponseReceived = true;
        this.checkVariantButtonsReady();
      }

      hasRestockIntegration() {
        return Boolean(
          this.actions?.querySelector('.restock-alerts-notify-button, .nm-portal') ||
            document.querySelector(
              'script[src*="restock-sdk-loader"], script[src*="cdn.notify-me.io"], script[src*="notify-me.io/sdk"]'
            )
        );
      }

      getRestockVariantMatch() {
        const variantId = String(
          this.currentVariant?.id || this.productForm?.querySelector('[name="id"]')?.value || ''
        ).trim();
        if (!/^\d+$/.test(variantId)) return null;

        const configuredVariants = window._ReStockConfig?.product?.variants;
        if (Array.isArray(configuredVariants)) {
          return configuredVariants.some((variant) => String(variant.id) === variantId);
        }

        const configText = document.getElementById('ReStock-config')?.textContent;
        const variantsIndex = configText?.indexOf('variants:');
        if (!configText || variantsIndex === -1) return null;

        const variantPattern = new RegExp(`\\bid\\s*:\\s*${variantId}\\s*,`);
        return variantPattern.test(configText.slice(variantsIndex));
      }

      shouldShowNotifyButton(isSoldOut, notifyButton) {
        if (this.isQuickAdd || !isSoldOut || !this.hasRestockIntegration()) return false;

        const restockVariantMatch = this.getRestockVariantMatch();
        if (restockVariantMatch !== null) return restockVariantMatch;

        return Boolean(notifyButton);
      }

      checkVariantButtonsReady() {
        if (!this.variantResponseReceived || !this.classList.contains('is-updating')) return;

        const notifyButton = !this.isQuickAdd
          ? this.actions?.querySelector('.restock-alerts-notify-button')
          : null;
        const mainText = this.mainButton?.querySelector('span')?.textContent?.trim();
        const isSoldOut =
          this.currentVariant?.available === false ||
          (this.mainButton?.disabled && mainText === window.variantStrings?.soldOut);
        const shouldShowNotifyButton = this.shouldShowNotifyButton(isSoldOut, notifyButton);

        if (shouldShowNotifyButton && !notifyButton) return;

        this.finishVariantUpdate();
      }

      finishVariantUpdate() {
        window.clearTimeout(this.variantUpdateFallbackTimer);
        this.variantResponseReceived = false;
        this.classList.remove('is-updating');
        this.actions?.removeAttribute('aria-busy');
      }

      observeMainButton() {
        if (!this.mainButton) return;

        this.buttonObserver = new MutationObserver(this.syncFromMainButton);
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });
      }

      observeMainPrice() {
        if (!this.mainPrice) return;

        this.priceObserver = new MutationObserver(() => this.syncPrice());
        this.priceObserver.observe(this.mainPrice, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ['class'],
        });
      }

      syncFromMainButton() {
        if (!this.button || !this.mainButton) return;

        const mainText = this.mainButton.querySelector('span')?.textContent?.trim();
        const isDisabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
        const isLoading = this.mainButton.classList.contains('loading');
        const currentVariant = this.currentVariant;
        const isSoldOut =
          currentVariant?.available === false || (isDisabled && mainText === window.variantStrings?.soldOut);

        if (mainText && this.buttonText) this.buttonText.textContent = mainText;

        this.button.disabled = isDisabled;
        this.button.toggleAttribute('aria-disabled', isDisabled);
        if (this.buyNowButton) {
          this.buyNowButton.disabled = isDisabled;
          this.buyNowButton.toggleAttribute('aria-disabled', isDisabled);
        }
        this.button.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
        this.soldOutBadge?.classList.toggle('hidden', !isSoldOut);
        this.classList.toggle('is-sold-out', isSoldOut);
        this.syncNotifyButton(isSoldOut);
      }

      syncNotifyButton(isSoldOut = this.classList.contains('is-sold-out')) {
        const notifyButton = this.actions?.querySelector('.restock-alerts-notify-button');
        const notifyPortal = notifyButton?.closest('.nm-portal');
        const shouldShowNotifyButton = this.shouldShowNotifyButton(isSoldOut, notifyButton);

        this.buyNowButton?.classList.toggle('hidden', shouldShowNotifyButton);
        this.notifyProxyButton?.classList.toggle('hidden', !shouldShowNotifyButton || Boolean(notifyButton));
        if (this.isQuickAdd) notifyButton?.classList.add('hidden');
        notifyPortal?.classList.toggle('hidden', !shouldShowNotifyButton);

        if (!shouldShowNotifyButton) {
          this.pendingNotifyClick = false;
          this.notifyProxyButton?.removeAttribute('aria-busy');
          return;
        }

        if (notifyButton && this.pendingNotifyClick) {
          this.pendingNotifyClick = false;
          this.notifyProxyButton?.removeAttribute('aria-busy');
          notifyButton.click();
        }
      }

      syncPrice() {
        if (!this.price || !this.mainPrice) return;

        const shouldHidePrice =
          this.mainPrice.classList.contains('hidden') || this.mainPrice.classList.contains('visibility-hidden');

        this.price.classList.toggle('hidden', shouldHidePrice);
        if (!shouldHidePrice) {
          this.price.innerHTML = this.mainPrice.innerHTML;
          this.stripCurrencyCode();
        }
      }

      stripCurrencyCode() {
        if (!this.price) return;

        const currencyCode = this.dataset.currencyCode || 'KRW';
        const escapedCurrencyCode = currencyCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const currencyCodePattern = new RegExp(`\\s*${escapedCurrencyCode}\\b`, 'g');

        this.price.querySelectorAll('.price-item').forEach((priceItem) => {
          priceItem.childNodes.forEach((node) => {
            if (node.nodeType !== Node.TEXT_NODE) return;

            node.textContent = node.textContent.replace(currencyCodePattern, '').replace(/\s{2,}/g, ' ').trim();
          });
        });
      }

      scheduleSummarySync() {
        if (this.summaryFrame) return;

        this.summaryFrame = window.requestAnimationFrame(() => {
          this.summaryFrame = null;
          this.syncFromMainButton();
          this.syncPrice();
          this.syncVariantImage();
        });
      }

      syncVariantImage() {
        if (!this.image) return;

        const variant = this.currentVariant;
        const imageSource = variant?.featured_image?.src || variant?.featured_media?.preview_image?.src;

        if (!imageSource) return;

        this.image.src = this.buildImageUrl(imageSource, 120);
        if (variant.featured_image?.alt) this.image.alt = variant.featured_image.alt;
      }

      buildImageUrl(src, width) {
        if (src.includes('width=')) return src;
        return `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
      }

      scheduleVisibilityUpdate() {
        if (this.visibilityFrame) return;

        this.visibilityFrame = window.requestAnimationFrame(() => {
          this.visibilityFrame = null;
          this.updateVisibility();
        });
      }

      updateVisibility() {
        if (!this.mainButton) {
          this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.observeMainButton();
        }

        if (!this.mainButton) return;

        this.updateStickyOffset();

        this.classList.add('is-visible');
        this.setAttribute('aria-hidden', 'false');
        document.body?.classList.add('product-sticky-atc-visible');
      }

      updateStickyOffset() {
        const height = Math.ceil(this.getBoundingClientRect().height || this.offsetHeight || 0);

        if (height > 0) {
          document.documentElement.style.setProperty('--product-sticky-atc-height', `${height}px`);
        }
      }

      get currentVariant() {
        const variantPicker = this.productInfo?.querySelector('variant-radios, variant-selects');
        if (variantPicker?.currentVariant) return variantPicker.currentVariant;

        const selectedVariantJson = variantPicker?.querySelector('[data-selected-variant]')?.textContent;
        if (selectedVariantJson) {
          try {
            return JSON.parse(selectedVariantJson);
          } catch (error) {
            console.error(error);
          }
        }

        const variantId = this.productInfo?.querySelector(`#${this.dataset.productFormId} [name="id"]`)?.value;
        const variantsJson = variantPicker?.querySelector('script[type="application/json"]')?.textContent;
        if (!variantId || !variantsJson) return null;

        try {
          return JSON.parse(variantsJson).find((variant) => String(variant.id) === String(variantId));
        } catch (error) {
          console.error(error);
          return null;
        }
      }

      get productForm() {
        return document.getElementById(this.dataset.productFormId);
      }

      get quantityInput() {
        return (
          this.productForm?.querySelector('[name="quantity"]') ||
          this.productInfo?.querySelector(`[name="quantity"][form="${this.dataset.productFormId}"]`)
        );
      }
    }
  );
}
